import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Calendar } from "lucide-react";

export default function NewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageTitle("News & Press Releases");

  useEffect(() => {
    api.get("/content/news").then(res => {
      setNews(res.data.filter(n => n.is_published !== false));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="news-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">News & Press Releases</h1>
          <p className="text-white/50 mt-3 text-base">Stay updated with the latest from JCI Bangladesh</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400">No news articles available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="news-grid">
            {news.map((item, i) => (
              <Link to={`/news/${item.id}`} key={item.id} data-testid={`news-card-${i}`}>
                <Card className="border-0 shadow-md overflow-hidden group cursor-pointer h-full hover:shadow-lg transition-shadow">
                  <div className="overflow-hidden h-52">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <p className="text-xs text-jci-blue font-semibold mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> {item.published_date}
                    </p>
                    <h3 className="font-bold text-jci-black text-base mb-2 line-clamp-2 group-hover:text-jci-blue transition-colors">{item.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-3">{item.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-jci-blue mt-4 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3 h-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
